import type { Random } from "../core/random.js";
import type { CompactResult } from "./model.js";
import { poisson } from "./rng.js";

/**
 * Calendario de solo ida: todos contra todos una vez (metodo del circulo).
 *
 * Devuelve, por jornada, parejas de indices `[local, visitante]`. Con 8
 * equipos salen 7 jornadas de 4 partidos, y cada equipo juega 3 o 4 en casa:
 * en una liga tan corta, jugar cinco fuera ya seria una injusticia.
 */
export function roundRobin(teams: number): Array<Array<[number, number]>> {
  if (teams % 2 !== 0) throw new Error("El calendario necesita un numero par de equipos");
  const order = Array.from({ length: teams }, (_, i) => i);
  const rounds: Array<Array<[number, number]>> = [];
  const homeGames = new Array<number>(teams).fill(0);
  const lastHome = new Array<boolean>(teams).fill(false);
  for (let round = 0; round < teams - 1; round++) {
    const games: Array<[number, number]> = [];
    for (let i = 0; i < teams / 2; i++) {
      const a = order[i]!;
      const b = order[teams - 1 - i]!;
      /*
       * Juega en casa quien menos lo ha hecho; a igualdad, quien jugo fuera la
       * ultima vez. Con la regla fija de "alternar por jornada" habia equipos
       * que no pisaban su campo en toda la temporada.
       */
      const aHome =
        homeGames[a]! !== homeGames[b]! ? homeGames[a]! < homeGames[b]! : lastHome[a] === lastHome[b] ? (round + i) % 2 === 0 : !lastHome[a];
      const [h, v] = aHome ? [a, b] : [b, a];
      homeGames[h]!++;
      lastHome[h] = true;
      lastHome[v] = false;
      games.push([h, v]);
    }
    rounds.push(games);
    // El primero se queda quieto y el resto gira.
    order.splice(1, 0, order.pop()!);
  }
  return rounds;
}

export interface TableRow {
  /** Indice en `members`. */
  index: number;
  club: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  /** Ultimos resultados, del mas viejo al mas nuevo. */
  form: Array<"win" | "draw" | "loss">;
}

/**
 * La clasificacion. Desempata por diferencia de goles, luego goles a favor, y
 * al final por el orden del sorteo: nunca por el nombre, que dejaria al
 * "Atletico" por delante del "Unión" para siempre.
 */
export function standings(members: readonly string[], results: readonly CompactResult[][]): TableRow[] {
  const rows: TableRow[] = members.map((club, index) => ({
    index,
    club,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    points: 0,
    form: [],
  }));
  for (const matchday of results) {
    for (const [h, a, hg, ag] of matchday) {
      const home = rows[h]!;
      const away = rows[a]!;
      home.played++;
      away.played++;
      home.goalsFor += hg;
      home.goalsAgainst += ag;
      away.goalsFor += ag;
      away.goalsAgainst += hg;
      if (hg > ag) {
        home.won++;
        away.lost++;
        home.points += 3;
        home.form.push("win");
        away.form.push("loss");
      } else if (hg < ag) {
        away.won++;
        home.lost++;
        away.points += 3;
        home.form.push("loss");
        away.form.push("win");
      } else {
        home.drawn++;
        away.drawn++;
        home.points++;
        away.points++;
        home.form.push("draw");
        away.form.push("draw");
      }
    }
  }
  return rows.sort(
    (x, y) =>
      y.points - x.points ||
      y.goalsFor - y.goalsAgainst - (x.goalsFor - x.goalsAgainst) ||
      y.goalsFor - x.goalsFor ||
      x.index - y.index,
  );
}

/**
 * Goles esperados por equipo y partido en cada division.
 *
 * Los modos pequeños meten mas: en un 3v3 de cinco minutos caen goles a
 * puñados, y un 11v11 es mas cerrado. Solo se usa para los partidos que NO
 * juegas tu; los tuyos los decide el campo.
 */
export const TIER_GOALS: readonly number[] = [2.4, 2.1, 1.7, 1.35];

/** Ventaja de jugar en casa, en goles esperados (multiplicador). */
const HOME_EDGE = 1.12;
/** Cuanto pesa la diferencia de fuerza. 20 puntos ≈ el doble de goles. */
const RATING_SCALE = 29;

export function simulateScore(
  random: Random,
  homeRating: number,
  awayRating: number,
  tier: number,
): [number, number] {
  const base = TIER_GOALS[tier] ?? TIER_GOALS[TIER_GOALS.length - 1]!;
  const diff = (homeRating - awayRating) / RATING_SCALE;
  const lambdaHome = base * Math.exp(diff / 2) * HOME_EDGE;
  const lambdaAway = base * Math.exp(-diff / 2);
  return [poisson(random, lambdaHome), poisson(random, lambdaAway)];
}
